import { merge } from 'lodash-es';
import * as yup from 'yup';

/** Display label shown in the Source column of the admin GL grid. The wire
 *  value from the API is `'carta' | 'atlas'`, but "atlas" is an internal
 *  project name we don't surface to operators — `getGeneralLedgerUploadData`
 *  translates wire values to these labels before they hit the grid.
 *  - 'Carta' rows come from `/file-upload/carta/general-ledger/status`
 *    (legacy `CartaGlFileEntity` — byte-size in fileSize, real createdBy email)
 *  - 'SS&C / other FAs' rows come from `/atlas/gl-deliveries`
 *    (FA-agnostic direct-upload pipeline — fileSize is "1,234 rows" since the
 *    underlying storage doesn't track byte size; createdBy has the
 *    `operator:`/`auto:`/`agent:` prefix stripped server-side so the
 *    userByEmail formatter works) */
export type GeneralLedgerUploadSource = 'Carta' | 'SS&C / other FAs';

export function generalLedgerUploadDataFields() {
  return {
    fileName: yup.string().required(),
    fileFormat: yup.string().required(),
    // Two formats coexist: Carta uploads emit MB ('0.50 MB'), Atlas uploads emit
    // a row count ('938 rows') because Atlas storage doesn't track byte size.
    // The 'Size' header is honest about both — 'File Size' would lie about
    // half the rows.
    fileSize: yup.string().required().label('Size'),
    createdAt: yup.date().required(),
    createdBy: yup.string().required().customMeta({
      formatter: 'userByEmail',
    }),
    // Nullable: Atlas-pipeline rows have no coverageStart until either an
    // operator declares one or rows extract a gl_date. The grid renders null
    // as "—". Carta rows always carry a real date (filename-extracted) so they
    // never hit the null path.
    startDate: yup.date().nullable().defined(),
    // No `.default()` — the default never fires anyway: every row goes through
    // `getGeneralLedgerUploadData` which sets `source` via SOURCE_LABEL[wire]
    // before reaching the grid. One source of truth.
    source: yup.string().oneOf<GeneralLedgerUploadSource>(['Carta', 'SS&C / other FAs']),
  };
}

export function generalLedgerUploadDataSchema() {
  return yup.object().shape(generalLedgerUploadDataFields());
}

/** `sourceObject` is data-only, not a grid column — it's surfaced inline in
 *  parens next to `source` (see `GeneralLedgerUploadsGrid`). Kept out of the
 *  yup schema so `schemaToGrid` doesn't auto-generate a column for it. */
export type GeneralLedgerUploadData = yup.InferType<ReturnType<typeof generalLedgerUploadDataSchema>> & {
  sourceObject?: string;
};

export function createGeneralLedgerUploadData(
  overrides: Partial<GeneralLedgerUploadData> = {}
): GeneralLedgerUploadData {
  return merge({}, generalLedgerUploadDataSchema().getDefault(), {
    fileName: 'test',
    fileFormat: 'test',
    fileSize: 'test',
    createdAt: new Date(0),
    createdBy: 'test',
    startDate: new Date(0),
    source: 'Carta' as GeneralLedgerUploadSource,
    ...overrides,
  });
}
